import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { FormEvent, useEffect, useMemo, useState } from 'react';

export type AddClassPayload = {
  term: string;
  class_name: string;
  course_code: string;
  section_code: string;
  start_date?: string;
  end_date?: string;
  educator_user_id?: string;
};

type EducatorOption = {
  user_id: string;
  label: string;
};

interface AddClassDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: AddClassPayload) => void;
  isSubmitting: boolean;
  title?: string;
  submitLabel?: string;
  educatorOptions?: EducatorOption[];
  requireEducatorSelection?: boolean;
}

function AddClassDialog({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  title = 'Create Class',
  submitLabel = 'Create Class',
  educatorOptions = [],
  requireEducatorSelection = false,
}: AddClassDialogProps) {
  const [term, setTerm] = useState('');
  const [className, setClassName] = useState('');
  const [courseCode, setCourseCode] = useState('MTH-161');
  const [sectionCode, setSectionCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEducatorUserId, setSelectedEducatorUserId] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const hasEligibleEducators = useMemo(
    () => !requireEducatorSelection || educatorOptions.length > 0,
    [educatorOptions.length, requireEducatorSelection]
  );

  useEffect(() => {
    if (!open) {
      setLocalError(null);
      return;
    }

    setTerm('');
    setClassName('');
    setCourseCode('MTH-161');
    setSectionCode('');
    setStartDate('');
    setEndDate('');
    setSelectedEducatorUserId('');
    setLocalError(null);
  }, [open]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError(null);

    if (!hasEligibleEducators) {
      setLocalError('No eligible educators are available to assign this class.');
      return;
    }

    if (requireEducatorSelection && !selectedEducatorUserId) {
      setLocalError('Please select an educator.');
      return;
    }

    onSubmit({
      term,
      class_name: className,
      course_code: courseCode,
      section_code: sectionCode,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      educator_user_id: requireEducatorSelection ? selectedEducatorUserId : undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1, alignItems: 'center' }}>
            {localError && <Alert severity="error" sx={{ width: '80%' }}>{localError}</Alert>}

            {requireEducatorSelection && (
              <FormControl sx={{ width: '80%' }} required>
                <InputLabel id="add-class-educator-label">Educator</InputLabel>
                <Select
                  labelId="add-class-educator-label"
                  label="Educator"
                  value={selectedEducatorUserId}
                  onChange={(e) => setSelectedEducatorUserId(e.target.value)}
                  disabled={!hasEligibleEducators}
                >
                  {educatorOptions.map((educator) => (
                    <MenuItem key={educator.user_id} value={educator.user_id}>
                      {educator.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField sx={{ width: '80%' }} label="Term" value={term} onChange={(e) => setTerm(e.target.value)} required />
            <TextField sx={{ width: '80%' }} label="Class Name" value={className} onChange={(e) => setClassName(e.target.value)} required />
            <TextField sx={{ width: '80%' }} label="Course Code" value={courseCode} onChange={(e) => setCourseCode(e.target.value)} required />
            <TextField sx={{ width: '80%' }} label="Section Code" value={sectionCode} onChange={(e) => setSectionCode(e.target.value)} required />
            <TextField
              sx={{ width: '80%' }}
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              sx={{ width: '80%' }}
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting || !hasEligibleEducators}>
            {isSubmitting ? 'Creating...' : submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export default AddClassDialog;
